import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Cloudinary, Transformation } from '@cloudinary/url-gen';
import { opacity } from '@cloudinary/url-gen/actions/adjust';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { colorize } from '@cloudinary/url-gen/actions/effect';
import { source } from '@cloudinary/url-gen/actions/overlay';
import { crop, fill, minimumFit, scale } from '@cloudinary/url-gen/actions/resize';
import { Position } from '@cloudinary/url-gen/qualifiers';
import { auto } from '@cloudinary/url-gen/qualifiers/quality';
import { image } from '@cloudinary/url-gen/qualifiers/source';
import { AllInOneSearchHeaderPageComponent, Client, JobMetaResponse } from '@interfaces';
import { Observable } from 'rxjs';
import { GtagService } from 'src/app/containers/gtag/gtag.service';
import { JobsSearchMetaService } from 'src/app/rest/jobs-search-meta.service';
import { SubdomainService } from 'src/app/rest/subdomain.service';
import { ComponentActionsService } from 'src/app/services/component-actions.service';
import { AbstractComponent } from '../abstract-component';

@Component({
  selector: 'app-all-in-one-search-header',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './all-in-one-search-header.component.html',
  styleUrls: ['./all-in-one-search-header.component.css']
})
export class AllInOneSearchHeaderComponent extends AbstractComponent {
    public component: AllInOneSearchHeaderPageComponent;
    public imageWidth = 1536;
    public imageHeight = 512;
    public img: string;
    public client$: Observable<Client>;
    public searchMeta: JobMetaResponse;
    public searchForm: UntypedFormGroup;
    public autocompleteFocus = false;

    constructor(
        protected actions: ComponentActionsService,
        @Inject(DOCUMENT) protected document,
        private subdomain: SubdomainService,
        private fb: UntypedFormBuilder,
        private meta: JobsSearchMetaService,
        private route: ActivatedRoute,
        private router: Router,
        private gtag: GtagService,
    ) {
        super(actions);
        this.client$ = this.subdomain.getClient();
        this.searchForm = this.fb.group({
            keywords: [],
            category: "",
            locationLabel: "",
        });
    }

    async ngOnInit(): Promise<void> {
        await this.fetchMetaData();
        this.calculateImage();
        this.setupForm();
    }

    private calculateImage() {
        if (!this.component.image?.public_id && !this.component.mobileImage?.public_id) return;
        const cld = new Cloudinary({ cloud: { cloudName: "minirobot" } });
        let scaleFn: any = scale;


        this.imageWidth = this.document?.documentElement?.clientWidth;
        this.imageHeight = this.document?.documentElement?.clientHeight;


        if (this.mobileMode) {
            this.imageHeight = 350;
        }

        switch (this.component.scaling) {
            case "fill":
                scaleFn = fill;
                break;
            case "crop":
                scaleFn = crop;
                break;
            case "fit":
                scaleFn = minimumFit;
                break;
            default:
                scaleFn = scale;
        }

        const img = cld
            .image(
                this.mobileMode
                    ? this.component.mobileImage?.public_id ?? this.component.image?.public_id
                    : this.component.image?.public_id
            )
            .delivery(quality(auto()))
            .delivery(format(auto()))
            .resize(scaleFn().width(this.imageWidth).height(this.imageHeight));

        if (!this.published) {
            img.overlay(
                source(
                    image("voyse:assets:draft").transformation(
                        new Transformation().effect(colorize(20).color("white")).adjust(opacity(20))
                    )
                ).position(new Position().allowOverflow(false))
            );
        }
        this.img = `url("${img.toURL()}")`;
    }

    private setupForm() {
        // Apply filters from query params.
        if (this.route.snapshot.queryParams?.keywords) {
            this.searchForm.get("keywords").setValue(this.route.snapshot.queryParams.keywords);
        }

        if (this.route.snapshot.queryParams?.category) {
            this.searchForm.get("category").setValue(this.route.snapshot.queryParams.category);
        }

        if (this.route.snapshot.queryParams?.locationLabel) {
            this.searchForm.get("locationLabel").setValue(this.route.snapshot.queryParams.locationLabel);
        }
    }

    public async fetchMetaData(): Promise<any> {
        const client = await this.subdomain.getClient().toPromise();
        const params = {
            employer: client._id,
        };
        this.searchMeta = await (this.meta.find(params).toPromise() as Promise<JobMetaResponse>);
    }

    @HostListener("document:keydown.enter", ["$event"])
    public onEnter(event: KeyboardEvent) {
        this.emitSearch();
    }

    public emitSearch() {
        this.gtag.fireSearchEvent(this.searchForm.value);
        this.search();
    }

    public search() {
        let redirectTo = [];
        const payload = this.searchForm.getRawValue();
        this.router.navigate(redirectTo, { relativeTo: this.route, queryParams: payload });
    }

    public clearSearch() {
        this.searchForm.reset();
    }
}
